/**
 * App Proxy transport for cart sync.
 *
 * Two rules that are easy to get wrong and impossible to notice if you do:
 *
 *   1. P1/P3 carry the cart key in the QUERY STRING; P2 carries it in the JSON
 *      BODY. Sending it in the wrong place is not an error — it reads and writes
 *      different rows, and the customer's cleared cart comes back forever.
 *   2. Shopify signs the query params we put in the URL, so our own params must
 *      be present when the storefront makes the request. We never add anything
 *      to the URL after the fact.
 */

import type { PublicCartState, PushBody, SyncResult } from "./types.js";
import { SyncFailure } from "./types.js";

const PROXY_BASE = "/apps/lagniappeserver";
const PULL_PATH = `${PROXY_BASE}/cart-sync`;
const HISTORY_PATH = `${PROXY_BASE}/cart-sync-history`;

/** Matches transitTimeService's posture: long enough to be useful, bounded. */
const REQUEST_TIMEOUT_MS = 10000;

function unavailable(status?: number, detail?: string): SyncResult<never> {
  return { ok: false, failure: SyncFailure.Unavailable, status, detail };
}

async function readJson(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return null;
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function isCartState(value: unknown): value is PublicCartState {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.version === "number" && Array.isArray(candidate.items)
  );
}

/**
 * Normalize a response into a SyncResult.
 *
 * Every non-200 maps to "no information", never to "the cart is empty". The
 * distinction matters: a 503 that was read as an empty cart would propagate a
 * wipe to every device.
 */
async function interpret(
  response: Response,
): Promise<SyncResult<PublicCartState>> {
  const body = await readJson(response);

  if (response.ok) {
    if (!isCartState(body)) {
      return unavailable(response.status, "unexpected body shape");
    }
    return { ok: true, value: body };
  }

  const errorBody = (body ?? {}) as { error?: string; details?: string; current?: unknown };

  switch (response.status) {
    case 400:
      // Our payload was wrong. A bug — never evidence about cart state.
      return {
        ok: false,
        failure: SyncFailure.BadRequest,
        status: 400,
        detail: errorBody.details ?? errorBody.error,
      };
    case 403:
      // Session ended mid-page, or the signature was rejected. Either way this
      // page must stop syncing rather than keep guessing.
      return {
        ok: false,
        failure: SyncFailure.Forbidden,
        status: 403,
        detail: errorBody.error,
      };
    case 404:
      return {
        ok: false,
        failure: SyncFailure.NotFound,
        status: 404,
        detail: errorBody.error,
      };
    case 409:
      return {
        ok: false,
        failure: SyncFailure.Conflict,
        status: 409,
        current: isCartState(errorBody.current) ? errorBody.current : undefined,
        detail: errorBody.error,
      };
    default:
      // 429, 500, 503 and anything else: no information, stay device-local.
      return unavailable(response.status, errorBody.error);
  }
}

async function request(
  url: string,
  init?: RequestInit,
): Promise<SyncResult<PublicCartState>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      ...init,
      cache: "no-store",
      signal: controller.signal,
    });
    return await interpret(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return unavailable(undefined, message);
  } finally {
    clearTimeout(timer);
  }
}

export interface PullParams {
  readonly locationId: number;
  readonly deviceId: string;
  /**
   * Read fresh from /cart.js on every pull. Registering it is the only thing
   * that lets the purchase webhook tombstone a device that pulled, applied, and
   * checked out without ever pushing.
   */
  readonly cartToken?: string | null;
}

/** P1 — current cart, history metadata, and the other-device activity hint. */
export function pullCart(params: PullParams): Promise<SyncResult<PublicCartState>> {
  const query = new URLSearchParams();
  query.set("locationId", String(params.locationId));
  query.set("deviceId", params.deviceId);
  if (params.cartToken) query.set("cartToken", params.cartToken);
  return request(`${PULL_PATH}?${query.toString()}`);
}

/** P2 — apply an op batch. The cart key travels in the body, never the query. */
export function pushOps(body: PushBody): Promise<SyncResult<PublicCartState>> {
  return request(PULL_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** P3 — one historical snapshot. 404 means it aged out of the 72-hour window. */
export function fetchHistoryVersion(
  locationId: number,
  version: number,
): Promise<SyncResult<PublicCartState>> {
  const query = new URLSearchParams();
  query.set("locationId", String(locationId));
  query.set("version", String(version));
  return request(`${HISTORY_PATH}?${query.toString()}`);
}

/**
 * Best-effort flush on unload, for the tombstone that must survive a reload.
 *
 * Must be a Blob with an explicit JSON content type: express.json() only parses
 * that, and a text/plain beacon arrives as {} and 400s. Returns false when the
 * browser refuses to queue it, in which case the caller must not update its
 * bookkeeping as though the push happened.
 */
export function beaconPush(body: PushBody): boolean {
  if (typeof navigator.sendBeacon !== "function") return false;
  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: "application/json",
    });
    return navigator.sendBeacon(PULL_PATH, blob);
  } catch {
    return false;
  }
}
